import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { deleteOneDriveDoc } from "../../api/documentsAPI";
import UserAuth from "../../../auth/userAuth";
import { logger } from "../../../utils/logger";
import {
  DocumentDelete,
  DELETE_FAILED_MESSAGE,
} from "../../constants/messageTypes";

const userAuth = new UserAuth();
const DELETE_DIALOG_AUTO_CLOSE_MS = 3000;

/**
 * Delete a single OneDrive working document.
 * Payload: { fileId, teamId, membershipType, documentDisplayName? }
 * Schedules clearDeleteWorkingDocumentState after 3s on success or failure (like documentCheckoutFlowSlice).
 */
export const deleteWorkingDocument = createAsyncThunk(
  "documents/deleteWorkingDocument",
  async (
    { fileId, teamId, membershipType, oneDriveRoot, documentDisplayName },
    { dispatch, rejectWithValue },
  ) => {
    logger.info(
      "deleteWorkingDocument start",
      { fileId, teamId, membershipType },
      "REDUX",
    );

    try {
      const tokenResponse = await userAuth.getUserToken(true);
      const accessToken = tokenResponse?.accessToken ?? null;

      if (!accessToken) {
        logger.error(
          "deleteWorkingDocument no access token",
          tokenResponse,
          "REDUX",
        );
        setTimeout(() => {
          dispatch(clearDeleteWorkingDocumentState());
        }, DELETE_DIALOG_AUTO_CLOSE_MS);
        return rejectWithValue({ message: "No access token" });
      }

      const { status, data } = await deleteOneDriveDoc(accessToken, {
        fileId,
        teamId,
        membershipType,
        oneDriveRoot,
      });

      if (status === 200 || status === 204) {
        logger.info(
          "deleteWorkingDocument success",
          { status, fileId },
          "REDUX",
        );
        setTimeout(() => {
          dispatch(clearDeleteWorkingDocumentState());
        }, DELETE_DIALOG_AUTO_CLOSE_MS);
        return {
          fileId,
          status,
          data,
          documentDisplayName: documentDisplayName ?? "Document",
        };
      }

      const responseMessage =
        typeof data === "string" && data
          ? data
          : data?.message && typeof data.message === "string"
            ? data.message
            : null;
      const message =
        responseMessage || `Delete failed with status ${status}`;
      logger.error(
        "deleteWorkingDocument unexpected status",
        { status, fileId, message: responseMessage || message },
        "REDUX",
      );
      setTimeout(() => {
        dispatch(clearDeleteWorkingDocumentState());
      }, DELETE_DIALOG_AUTO_CLOSE_MS);
      return rejectWithValue({ message });
    } catch (err) {
      logger.error("deleteWorkingDocument failed", err, "REDUX");
      const responseData = err?.response?.data;
      const responseMessage =
        typeof responseData === "string" && responseData
          ? responseData
          : responseData?.message && typeof responseData.message === "string"
            ? responseData.message
            : null;
      const message =
        responseMessage || err?.message || "Delete request failed.";
      setTimeout(() => {
        dispatch(clearDeleteWorkingDocumentState());
      }, DELETE_DIALOG_AUTO_CLOSE_MS);
      return rejectWithValue({ message });
    }
  },
);

const initialState = {
  loading: false,
  error: null,
  message: null,
  success: false,
  lastDeletedFileId: null,
  lastStatus: null,
};

const deleteWorkingDocumentSlice = createSlice({
  name: "documents/deleteWorkingDocument",
  initialState,
  reducers: {
    clearDeleteWorkingDocumentState: (state) => {
      state.loading = false;
      state.error = null;
      state.message = null;
      state.success = false;
      state.lastDeletedFileId = null;
      state.lastStatus = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(deleteWorkingDocument.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.message = null;
        state.success = false;
      })
      .addCase(deleteWorkingDocument.fulfilled, (state, action) => {
        const displayName =
          action.payload?.documentDisplayName ?? "Document";
        state.loading = false;
        state.error = null;
        state.message = DocumentDelete(displayName);
        state.success = true;
        state.lastDeletedFileId = action.payload?.fileId ?? null;
        state.lastStatus = action.payload?.status ?? null;
      })
      .addCase(deleteWorkingDocument.rejected, (state, action) => {
        const err = action.payload ?? action.error ?? null;
        state.loading = false;
        state.error = err;
        state.message =
          err?.message && typeof err.message === "string"
            ? err.message
            : DELETE_FAILED_MESSAGE;
        state.success = false;
      });
  },
});

export const { clearDeleteWorkingDocumentState } =
  deleteWorkingDocumentSlice.actions;

export default deleteWorkingDocumentSlice.reducer;

